/**
 * The one decision `applyHighDprCanvas()`'s (see `highDpr.ts`) per-frame
 * self-healing check makes - pure and framework-free (no Phaser/DOM
 * access, so no `import Phaser` transitively pulling in a browser-only
 * device-detection module that would crash this project's plain-Node
 * Vitest environment), same split this codebase already uses elsewhere
 * (`ViewportClassification.ts`, `BoardLayout.ts`) for logic worth locking
 * down with a real, directly-runnable test. See `highDpr.ts`'s own
 * `maybeCorrect()` for why BOTH the canvas/renderer size AND every
 * Scene's own camera size must be checked - checking only the former is
 * exactly the quarter-screen-on-scene-re-entry bug this function exists
 * to prevent a regression of.
 */
export interface HighDprSyncState {
  rendererWidth: number;
  rendererHeight: number;
  canvasWidth: number;
  canvasHeight: number;
  /** Every currently active Scene's own main camera dimensions - `null` for a Scene with no camera yet (mid-boot, or torn down). */
  cameraDimensions: readonly ({ width: number; height: number } | null)[];
}

/** Whether `state` needs a full DPR correction pass to reach `expected` (both in physical pixels) - true unless the canvas, the renderer, AND every camera already match. */
export function needsHighDprCorrection(state: HighDprSyncState, expected: { width: number; height: number }): boolean {
  const canvasOk =
    state.rendererWidth === expected.width && state.rendererHeight === expected.height && state.canvasWidth === expected.width && state.canvasHeight === expected.height;
  const camerasOk = state.cameraDimensions.every((camera) => !camera || (camera.width === expected.width && camera.height === expected.height));
  return !(canvasOk && camerasOk);
}
